import { useApiScope } from '@commenda/api/scope';
import { MultiFileUploader } from '@commenda/kakashi';
import type { ValidationRules } from '@commenda/types';
import { FileBasic } from '@commenda/types/src/file/types';
import { LegacyDocumentType } from '@prisma/client';
import { useCallback, useContext, useMemo, useState } from 'react';
import { DEFAULT_MAX_FILE_SIZE_MB } from '../../dsl-constants';
import { FormContext } from '../../hooks/FormContext';
import { buildShareScopedFileUpload } from '../../utils/share-scoped-upload';
import { isRuleActive } from '../../utils/validation';
import { LabelWithGuidanceTooltip } from './LabelWithGuidanceTooltip';

export const MultiFileUploadComponent = ({
  fieldId,
  label,
  description,
  props = {},
  guidance,
  validation,
}: {
  fieldId: string;
  label: string;
  description?: string;
  props?: {
    acceptedFileTypes?: string[];
    maxSizeInMB?: number;
    maxFiles?: number;
    message?: string;
    tooltip?: string;
    disabled?: boolean;
    documentType?: LegacyDocumentType;
  };
  guidance?: { text?: string };
  validation?: {
    rules?: ValidationRules;
    triggers?: string[];
  };
}) => {
  const { shareToken } = useApiScope();
  const shareScopedUpload = useMemo(
    () => buildShareScopedFileUpload(shareToken),
    [shareToken]
  );
  const {
    formData,
    updateField,
    errors,
    validateField,
    setFieldUploading,
    isFieldVisible,
    isFieldDisabled,
  } = useContext(FormContext);
  const disabled = (props.disabled as boolean) || isFieldDisabled(fieldId);

  const handleUploadingChange = useCallback(
    (isUploading: boolean) => {
      setFieldUploading(fieldId, isUploading);
    },
    [fieldId, setFieldUploading]
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const files = (formData[fieldId] as FileBasic[]) || [];
  const {
    acceptedFileTypes = [],
    maxSizeInMB = DEFAULT_MAX_FILE_SIZE_MB,
    maxFiles,
    message,
    tooltip,
    documentType = LegacyDocumentType.Other,
  } = props;

  const isRequired = isRuleActive(validation?.rules?.required, isFieldVisible);

  // Build accept object with support for multiple MIME types per extension
  // CSV and Excel files can have multiple MIME types depending on the system/browser
  const accept: Record<string, string[]> = {};

  // Helper to add extension to a MIME type
  const addAccept = (mimeType: string, ext: string) => {
    if (!accept[mimeType]) accept[mimeType] = [];
    if (!accept[mimeType].includes(ext)) accept[mimeType].push(ext);
  };

  acceptedFileTypes.forEach((type: string) => {
    if (type === '.csv') {
      // CSV files can be reported with many different MIME types depending on browser/OS
      addAccept('text/csv', '.csv');
      addAccept('application/csv', '.csv');
      addAccept('text/plain', '.csv'); // Some systems report CSV as plain text
      addAccept('text/x-csv', '.csv');
      addAccept('application/x-csv', '.csv');
      addAccept('application/vnd.ms-excel', '.csv'); // Windows often reports CSV as Excel
    } else if (type === '.xls') {
      addAccept('application/vnd.ms-excel', '.xls');
      addAccept('application/x-msexcel', '.xls');
    } else if (type === '.xlsx') {
      // XLSX files can be reported with multiple MIME types
      addAccept(
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        '.xlsx'
      );
      addAccept('application/vnd.ms-excel', '.xlsx'); // Some browsers report this
      addAccept('application/octet-stream', '.xlsx'); // Fallback when MIME type unknown
    } else if (type === '.pdf') {
      addAccept('application/pdf', '.pdf');
    } else if (type === '.doc') {
      addAccept('application/msword', '.doc');
    } else if (type === '.docx') {
      addAccept(
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        '.docx'
      );
    } else if (type === '.jpg' || type === '.jpeg') {
      addAccept('image/jpeg', type);
    } else if (type === '.png') {
      addAccept('image/png', '.png');
    } else if (type === '.txt') {
      addAccept('text/plain', '.txt');
    } else if (type === '.json') {
      addAccept('application/json', '.json');
      addAccept('text/json', '.json');
    }
  });

  const handleFilesChange = (newFiles: FileBasic[]) => {
    updateField(fieldId, newFiles);
    if (validation?.rules) {
      validateField(fieldId, newFiles, validation.rules);
    }
  };

  const [uploadError, setUploadError] = useState<string>('');

  const handleError = () => {
    // Error handling is done via setErrorMessage in MultiFileUploader
    setUploadError('Failed to upload file. Please try again.');
  };

  return (
    <div className="mb-4">
      {(label || description) && (
        <LabelWithGuidanceTooltip
          label={label}
          description={description}
          guidance={guidance?.text}
          isRequired={isRequired}
        />
      )}
      <MultiFileUploader
        files={files}
        onFileUpload={shareScopedUpload}
        documentType={documentType}
        onError={handleError}
        accept={accept}
        maxSize={maxSizeInMB * 1024 * 1024}
        maxFiles={maxFiles}
        message={
          message ||
          `Max file size: ${maxSizeInMB}MB. Accepted formats: ${acceptedFileTypes.join(', ')}`
        }
        onFilesChange={handleFilesChange}
        disabled={disabled}
        companyId={undefined}
        token={undefined}
        setErrorMessage={setUploadError}
        onUploadingChange={handleUploadingChange}
      />
      {tooltip && (
        <p className="mt-1 txt-small-regular text-text-tertiary">{tooltip}</p>
      )}
      {(errors[fieldId] || uploadError) && (
        <p className="mt-1 txt-small-regular text-fg-error">
          {errors[fieldId] || uploadError}
        </p>
      )}
    </div>
  );
};
